import { TurnkeySDK } from './index.js';
import { Web3ActionData, ActionParams, ActionResponse, AuthenticatorType, WalletAuthenticator } from '@osiris-ai/sdk';
import { getChainIdFromAddressFormat, TurnkeyConfig } from './types';
import { CreateWalletRequest, CreateWalletResponse, GetWalletRequest, GetWalletResponse, AddAccountRequest, AddAccountResponse } from '@osiris-ai/sdk';
import { CreateWalletRequest as TurnkeyCreateWalletRequest, AddWalletAccountsRequest as TurnkeyAddWalletAccountsRequest } from './types.js';

export class TurnkeyAuthenticator extends WalletAuthenticator {
    sdk: TurnkeySDK;

    constructor(config: TurnkeyConfig) {
        super('turnkey', config);
        this.sdk = new TurnkeySDK(config);
    }

    async getWallet(params: GetWalletRequest): Promise<GetWalletResponse> {
        const walletAccountsResponse = await this.sdk.listWalletAccounts({
            organizationId: params.id,
            walletId: params.accountId,
            paginationOptions: {
                limit: '100',
                before: '',
                after: ''
            }
        });

		const account = {
			id: params.id,
			addresses: walletAccountsResponse.accounts.map((account) => ({
				chains: [getChainIdFromAddressFormat(account.addressFormat)],
				address: account.address,
				derivationPath: account.path,
				curve: account.curve,
				addressFormat: account.addressFormat
			})),
			chains: [getChainIdFromAddressFormat(walletAccountsResponse.accounts[0]!.addressFormat)],
			mnemonicLength: 24
		}

        return {
            id: params.accountId,
            name: "",
            accounts: account,
            authenticators: []
        };
    }

    async addAccount(params: AddAccountRequest): Promise<AddAccountResponse> {
		const addAccountsResponse = await this.sdk.addWalletAccount({
            type: 'ACTIVITY_TYPE_CREATE_WALLET_ACCOUNTS',
            timestampMs: Date.now().toString(),
            organizationId: params.walletId,
            parameters: {
                walletId: params.accountId,
                accounts: params.addresses.map((acc) => ({
                    pathFormat: acc.pathFormat,
                    path: acc.path,
                    curve: acc.curve,
                    addressFormat: acc.addressFormat
                }))
            }
        });

		if(!addAccountsResponse) {
			throw new Error('Failed to add account');
		}

        return this.getWallet({
			id: params.walletId,
			accountId: params.accountId
		});
    }

    async createWallet(params: CreateWalletRequest): Promise<CreateWalletResponse> {
        const turnkeyRequest: TurnkeyCreateWalletRequest = {
            subOrganizationName: params.name,
            rootUsers: [
				{
					userName: params.name,
					apiKeys: params.authenticators.filter((authenticator) => authenticator.type === AuthenticatorType.API_KEY).map((authenticator) => ({
						apiKeyName: authenticator.credentials.apiKeyName,
						publicKey: authenticator.credentials.publicKey,
						curveType: authenticator.credentials.curveType
					})) ?? [],
					authenticators: params.authenticators.filter((authenticator) => authenticator.type === AuthenticatorType.WEBAUTHN).map((authenticator) => ({
						authenticatorName: authenticator.credentials.authenticatorName,
						challenge: authenticator.credentials.challenge,
						attestation: authenticator.credentials.attestation
					})) ?? [],
					oauthProviders: params.authenticators.filter((authenticator) => authenticator.type === AuthenticatorType.OAUTH).map((authenticator) => ({
						oauthProviderName: authenticator.credentials.oauthProviderName,
						oidcToken: authenticator.credentials.oidcToken
					})) ?? []
				}
			],
            rootQuorumThreshold: 1,
            wallet: {
                walletName: params.name,
                accounts: params.accounts.map((acc) => ({
                    pathFormat: acc.pathFormat,
                    path: acc.path,
                    curve: acc.curve,
                    addressFormat: acc.addressFormat
                })),
                mnemonicLength: 12
            }
        };
        const response = await this.sdk.createWallet(turnkeyRequest);

        const responseAddresses = response.activity.result.createSubOrganizationResultV7.wallet.addresses;
        const allChains = Array.from(new Set(params.accounts.flatMap(acc => acc.chains)));
        const addresses = params.accounts.map((acc, idx) => ({
            chains: acc.chains,
            address: responseAddresses && responseAddresses[idx] ? responseAddresses[idx] : '',
            derivationPath: acc.path,
            curve: acc.curve,
            addressFormat: acc.addressFormat
        }));

		const account = {
            id: response.activity.result.createSubOrganizationResultV7.wallet.walletId,
            addresses,
            chains: allChains,
            mnemonicLength: 24
        };
        return {
            id: response.activity.result.createSubOrganizationResultV7.subOrganizationId,
            name: params.name,
            accounts: account,
            authenticators: [] // TODO: Not available from Turnkey
        };
    }

    async action(params: ActionParams<Web3ActionData>): Promise<ActionResponse> {
        if (!params.data) {
            throw new Error('No data provided');
        }

        const { method, walletId, walletAddress, payload, chain } = params.data;
		if(!method || !walletId || !walletAddress || !payload) {
			throw new Error(`Invalid parameters, missing ${!method ? 'method' : !walletId ? 'walletId' : !walletAddress ? 'walletAddress' : 'payload'}`);
		}

        try {
            switch (method) {
                case 'signMessage':
                    return await this.handleSignMessage(walletId, walletAddress, payload);

                case 'signTypedData':
                    return await this.handleSignTypedData(walletId, walletAddress, payload);

                case 'signTransaction':
                    return await this.handleSignTransaction(
                        walletId,
                        walletAddress,
                        payload,
                        chain
                    );

                case 'signRawPayloads':
                    return await this.handleSignRawPayloads(walletId, walletAddress, payload);

                default:
                    throw new Error(`Unsupported method: ${method}`);
            }
        } catch (error: any) {
            console.error(`Action execution failed:`, error.message);
            throw new Error(`Failed to execute ${method}: ${error.response ? JSON.stringify(error.response.data) : error.message}`);
        }
    }

	private async handleSignMessage(
        walletId: string,
        walletAddress: string,
        payload: any
    ): Promise<ActionResponse> {
        const tx = await this.sdk.signRawPayloads({
            organizationId: walletId,
            timestampMs: Date.now().toString(),
            type: 'ACTIVITY_TYPE_SIGN_RAW_PAYLOADS',
            parameters: {
                signWith: walletAddress,
                payloads: Array.isArray(payload) ? payload : [payload],
                encoding: 'PAYLOAD_ENCODING_HEXADECIMAL',
                hashFunction: 'HASH_FUNCTION_NO_OP',
            },
        });

        if (tx.activity.status === 'ACTIVITY_STATUS_COMPLETED') {
            return {
                data: tx.activity.result.signRawPayloadsResult,
                status: 200,
                statusText: 'OK',
            };
        }

        throw new Error('Failed to sign message');
    }

    private async handleSignTypedData(
        walletId: string,
        walletAddress: string,
        payload: any
    ): Promise<ActionResponse> {
        let typedData = payload;
        if (Array.isArray(payload)) {
            if (payload.length === 0) {
                throw new Error('Empty payload array provided for typed data signing');
            }
            typedData = payload[0];
        }

        if (!typedData.domain || !typedData.types || !typedData.primaryType || !typedData.message) {
            throw new Error('Invalid EIP-712 typed data structure: missing required fields (domain, types, primaryType, message)');
        }

        const typedDataString = JSON.stringify(typedData);

        const tx = await this.sdk.signRawPayload({
            organizationId: walletId,
            timestampMs: Date.now().toString(),
            type: 'ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2',
            parameters: {
                signWith: walletAddress,
                payload: typedDataString,
                encoding: 'PAYLOAD_ENCODING_EIP712',
                hashFunction: 'HASH_FUNCTION_NO_OP',
            },
        });

        if (tx.activity.status === 'ACTIVITY_STATUS_COMPLETED') {
            return {
                data: {
                    signatures: [tx.activity.result.signRawPayloadResult],
                    originalTypedData: typedData,
                },
                status: 200,
                statusText: 'OK',
            };
        }

        throw new Error('Failed to sign typed data');
    }

    private async handleSignTransaction(
        walletId: string,
        walletAddress: string,
        payload: any,
        chain?: string
    ): Promise<ActionResponse> {
        if (!payload.serializedTransaction) {
            throw new Error('Missing serializedTransaction in payload');
        }

        const transactionType = chain?.startsWith('evm')
            ? 'TRANSACTION_TYPE_ETHEREUM'
            : 'TRANSACTION_TYPE_SOLANA';

        const tx = await this.sdk.signTransaction({
            type: 'ACTIVITY_TYPE_SIGN_TRANSACTION_V2',
            timestampMs: Date.now().toString(),
            organizationId: walletId,
            parameters: {
                signWith: walletAddress,
                unsignedTransaction: payload.serializedTransaction,
                type: transactionType,
            },
        });

        if (tx.activity.status === 'ACTIVITY_STATUS_COMPLETED') {
            return {
                data: tx.activity.result.signTransactionResult,
                status: 200,
                statusText: 'OK',
            };
        }

        throw new Error('Failed to sign transaction');
    }

    private async handleSignRawPayloads(
        walletId: string,
        walletAddress: string,
        payload: any
    ): Promise<ActionResponse> {
        const payloads = Array.isArray(payload) ? payload : [payload];

        const tx = await this.sdk.signRawPayloads({
            organizationId: walletId,
            timestampMs: Date.now().toString(),
            type: 'ACTIVITY_TYPE_SIGN_RAW_PAYLOADS',
            parameters: {
                signWith: walletAddress,
                payloads,
                encoding: 'PAYLOAD_ENCODING_HEXADECIMAL',
                hashFunction: 'HASH_FUNCTION_NO_OP',
            },
        });

        if (tx.activity.status === 'ACTIVITY_STATUS_COMPLETED') {
            return {
                data: tx.activity.result.signRawPayloadsResult,
                status: 200,
                statusText: 'OK',
            };
        }

        throw new Error('Failed to sign raw payloads');
    }


}